import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, LogOut, Check, X, RefreshCw, CalendarDays, Car, Mail, Phone, Warehouse, Truck, Loader2, Landmark, ArrowLeft, MessagesSquare, Smartphone, Star, Package, CalendarOff } from "lucide-react";
import { Input } from "../ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Logo } from "./Navbar";
import AdminQuestions from "./AdminQuestions";
import AdminReviews from "./AdminReviews";
import AdminPackages from "./AdminPackages";
import AdminClosures from "./AdminClosures";
import QuoteDialog, { QuoteSummary } from "./AdminQuote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "evt_admin_token";

const BOOKING_BADGE = {
  pending: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  confirmed: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300",
  declined: "border-white/15 bg-white/5 text-white/50",
};
const PAY_LABEL = {
  not_required: ["No deposit", "text-white/50"],
  paid: ["Deposit paid", "text-emerald-300"],
  awaiting_transfer: ["Awaiting bank transfer", "text-amber-300"],
  refunded: ["Refunded", "text-white/50"],
  refund_due: ["Refund due (bank)", "text-brand"],
  cancelled: ["Cancelled", "text-white/50"],
};
// Swap to the admin web-app manifest so "Add to Home Screen" opens straight into /admin
const useAdminAppMode = () => {
  useEffect(() => {
    const link = document.getElementById("app-manifest");
    const title = document.getElementById("app-title");
    const prevHref = link?.getAttribute("href");
    const prevTitle = title?.getAttribute("content");
    const prevDoc = document.title;
    link?.setAttribute("href", "/admin-manifest.json");
    title?.setAttribute("content", "EVT Admin");
    document.title = "EVTTuning Admin";
    return () => {
      if (prevHref) link?.setAttribute("href", prevHref);
      if (prevTitle) title?.setAttribute("content", prevTitle);
      document.title = prevDoc;
    };
  }, []);
};

const InstallHint = () => {
  const [prompt, setPrompt] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("evt_admin_install_hidden") === "1" || window.matchMedia?.("(display-mode: standalone)").matches);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (hidden) return null;
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const hide = () => { localStorage.setItem("evt_admin_install_hidden", "1"); setHidden(true); };
  return (
    <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-brand/30 bg-brand/[0.06] p-5" data-testid="install-hint">
      <div className="flex items-start gap-3">
        <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
        <div>
          <div className="font-semibold text-white">Add EVT Admin to your phone</div>
          <p className="mt-0.5 text-sm text-white/60">
            {isIOS ? <>In Safari, tap <span className="font-semibold text-white">Share</span> then <span className="font-semibold text-white">Add to Home Screen</span>.</>
              : prompt ? "Install it as an app for one-tap access to bookings and messages."
              : <>In Chrome, open the <span className="font-semibold text-white">⋮ menu</span> and tap <span className="font-semibold text-white">Add to Home screen</span> or <span className="font-semibold text-white">Install app</span>.</>}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {prompt && <button onClick={async () => { prompt.prompt(); await prompt.userChoice; setPrompt(null); hide(); }} className="btn-red rounded-lg px-4 py-2 text-sm font-semibold" data-testid="install-app-btn">Install app</button>}
        <button onClick={hide} className="rounded-lg border border-white/15 px-4 py-2 text-sm text-white/70 hover:border-white/40" data-testid="install-hint-dismiss">Got it</button>
      </div>
    </div>
  );
};

const endOf = (t) => `${String(parseInt(t, 10) + 1).padStart(2, "0")}:00`;

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#08090b] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111217] p-8" data-testid="admin-login-form">
        <Logo />
        <h1 className="mt-6 font-display text-3xl font-bold text-white">Admin Login</h1>
        <p className="mt-1 text-sm text-white/55">Manage and confirm bookings.</p>
        <div className="relative mt-6">
          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password"
            className="h-11 border-white/10 bg-[#0b0c0f] pl-9 text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0" data-testid="admin-password-input" />
        </div>
        <button disabled={loading || !pw} className="btn-red mt-5 flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-60" data-testid="admin-login-btn">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Log in
        </button>
        <Link to="/" className="mt-5 flex items-center justify-center gap-1 text-sm text-white/50 hover:text-brand"><ArrowLeft className="h-4 w-4" />Back to site</Link>
      </form>
    </div>
  );
};

const BookingCard = ({ b, busy, onConfirm, onDecline, onMarkPaid, onQuote, token, onChanged }) => {
  const [payText, payCls] = PAY_LABEL[b.payment_status] || [b.payment_status, "text-white/60"];
  const car = [b.make, b.model, b.year].filter(Boolean).join(" ") || "Vehicle not specified";
  return (
    <div className="rounded-2xl border border-white/10 bg-[#111217] p-5 transition-colors hover:border-white/20" data-testid={`booking-${b.reference}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-xl font-bold text-white">{b.name}</span>
            <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase ${BOOKING_BADGE[b.booking_status]}`} data-testid={`booking-status-${b.reference}`}>{b.booking_status}</span>
          </div>
          <div className="mt-0.5 text-[13px] text-white/45">{b.reference}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1.5 font-semibold text-white"><CalendarDays className="h-4 w-4 text-brand" />{b.preferred_date}</div>
          <div className="text-sm text-white/60">{b.preferred_time} – {endOf(b.preferred_time)}</div>
        </div>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-white/70 sm:grid-cols-2">
        <span className="flex items-center gap-2"><Car className="h-4 w-4 text-white/40" />{car} · {b.service || "—"}</span>
        <span className="flex items-center gap-2">{b.service_type === "mobile" ? <Truck className="h-4 w-4 text-white/40" /> : <Warehouse className="h-4 w-4 text-white/40" />}{b.service_type === "mobile" ? "Mobile service" : "Workshop visit"} · <span className={payCls}>{payText}</span></span>
        <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-brand"><Mail className="h-4 w-4 text-white/40" />{b.email}</a>
        {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-brand"><Phone className="h-4 w-4 text-white/40" />{b.phone}</a>}
      </div>
      {b.details && <p className="mt-3 rounded-lg bg-white/[0.03] p-3 text-sm text-white/60">{b.details}</p>}
      {b.quote && <QuoteSummary q={b.quote} token={token} onChanged={onChanged} />}
      {b.booking_status !== "declined" && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
          {b.booking_status === "pending" && <>
          <button disabled={busy} onClick={() => onConfirm(b)} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60" data-testid={`confirm-${b.reference}`}><Check className="h-4 w-4" />Confirm & email</button>
          <button disabled={busy} onClick={() => onDecline(b)} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-brand hover:text-brand disabled:opacity-60" data-testid={`decline-${b.reference}`}><X className="h-4 w-4" />Decline</button>
          </>}
          {b.from_question_ref && <span className="self-center text-sm text-white/50" data-testid={`booking-from-quote-${b.reference}`}>Price & payment on quote {b.from_question_ref} (Questions tab)</span>}
          {!["pending", "expired", "failed"].includes(b.payment_status) && !b.from_question_ref && !b.quote?.balance_paid && (
            <button disabled={busy} onClick={() => onQuote(b)} className="flex items-center gap-1.5 rounded-lg border border-brand/50 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand/15 disabled:opacity-60" data-testid={`quote-${b.reference}`}>{b.quote ? "New quote / invoice" : "Send quote / invoice"}</button>
          )}
          {b.payment_status === "awaiting_transfer" && (
            <button disabled={busy} onClick={() => onMarkPaid(b)} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:border-white/40 disabled:opacity-60" data-testid={`markpaid-${b.reference}`}><Landmark className="h-4 w-4" />Mark transfer received</button>
          )}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  useAdminAppMode();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [view, setView] = useState("bookings");
  const [qCounts, setQCounts] = useState({});
  const [rCounts, setRCounts] = useState({});

  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }, []);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/enquiries`, { params: tab === "all" ? {} : { status: tab }, headers: { Authorization: `Bearer ${token}` } });
      setData(d);
    } catch (err) {
      if (err?.response?.status === 401) { toast.error("Session expired"); logout(); }
    } finally { setLoading(false); }
  }, [token, tab, logout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/questions`, { params: { status: "open" }, headers: { Authorization: `Bearer ${token}` } })
      .then(({ data: d }) => setQCounts(d.counts)).catch(() => {});
    axios.get(`${API}/admin/reviews`, { params: { status: "pending" }, headers: { Authorization: `Bearer ${token}` } })
      .then(({ data: d }) => setRCounts(d.counts)).catch(() => {});
  }, [token]);

  const act = async (b, action, msg) => {
    setBusyId(b.id);
    try {
      await axios.post(`${API}/admin/enquiries/${b.id}/${action}`, {}, auth);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  if (!token) return <Login onLogin={setToken} />;

  const tabs = [["pending", "Pending"], ["confirmed", "Confirmed"], ["declined", "Declined"], ["all", "All"]];
  return (
    <div className="min-h-screen bg-[#08090b]">
      <header className="border-b border-white/10 bg-[#0b0c0f]">
        <div className="container-x flex h-[72px] items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="rounded-md bg-white/10 px-2 py-0.5 text-[11px] font-bold uppercase text-white/70">Admin</span></div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-brand">View site</Link>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-white/60 hover:text-brand" data-testid="admin-logout"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>
      <main className="container-x py-10">
        <InstallHint />
        <div className="mb-8 grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-[#111217] p-1 sm:inline-grid sm:grid-cols-5" data-testid="admin-view-toggle">
          {[["bookings", "Bookings", CalendarDays, data.counts?.pending], ["questions", "Questions", MessagesSquare, qCounts?.open], ["reviews", "Reviews", Star, rCounts?.pending], ["packages", "Packages", Package, 0], ["closures", "Closed days", CalendarOff, 0]].map(([v, l, Icon, n]) => (
            <button key={v} onClick={() => setView(v)} className={`flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${view === v ? "bg-brand text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-view-${v}`}>
              <Icon className="h-4 w-4" />{l}
              {!!n && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${view === v ? "bg-white/25" : "bg-brand/20 text-brand"}`}>{n}</span>}
            </button>
          ))}
        </div>
        {view === "packages" ? <AdminPackages token={token} /> :
         view === "closures" ? <AdminClosures token={token} /> :
         view === "reviews" ? <AdminReviews token={token} onUnauthorized={logout} onCounts={setRCounts} /> :
         view === "questions" ? <AdminQuestions token={token} onUnauthorized={logout} onCounts={setQCounts} /> : (<>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-white">Bookings</h1>
            <p className="mt-1 text-white/55">Confirm requests to email customers. Declining releases the slot and refunds card deposits.</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2 text-sm text-white/80 hover:border-white/40" data-testid="admin-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
        </div>
        <div className="mt-8 grid grid-cols-3 gap-4">
          {[["pending", "Pending", "text-amber-300"], ["confirmed", "Confirmed", "text-emerald-300"], ["declined", "Declined", "text-white/60"]].map(([k, l, c]) => (
            <div key={k} className="rounded-xl border border-white/10 bg-[#111217] p-5">
              <div className="text-[12px] uppercase tracking-[0.1em] text-white/45">{l}</div>
              <div className={`mt-1 font-display text-3xl font-bold ${c}`} data-testid={`count-${k}`}>{data.counts?.[k] ?? 0}</div>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-brand bg-brand text-white" : "border-white/15 text-white/70 hover:text-white"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {data.items.map((b) => (
            <BookingCard key={b.id} b={b} busy={busyId === b.id}
              onConfirm={(x) => act(x, "confirm", `Confirmed · email sent to ${x.email}`)}
              onDecline={setDeclineTarget}
              onQuote={setQuoteTarget} token={token} onChanged={load} onMarkPaid={(x) => act(x, "mark-paid", "Bank transfer marked as received")} />
          ))}
        </div>
        {!loading && data.items.length === 0 && <div className="mt-6 rounded-2xl border border-dashed border-white/10 p-12 text-center text-white/45" data-testid="admin-empty">No {tab === "all" ? "" : tab} bookings.</div>}
        </>)}
      </main>

      <QuoteDialog booking={quoteTarget} token={token} onClose={() => setQuoteTarget(null)} onSent={() => { setQuoteTarget(null); load(); }} />
      <AlertDialog open={!!declineTarget} onOpenChange={(o) => !o && setDeclineTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#111217] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Decline this booking?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {declineTarget && <>{declineTarget.name} · {declineTarget.preferred_date} {declineTarget.preferred_time}. The slot will be released and the customer emailed.
                {declineTarget.payment_method === "card" && declineTarget.payment_status === "paid" && ` The £${Number(declineTarget.deposit_amount || 0).toFixed(2)} card deposit will be refunded automatically.`}
                {declineTarget.payment_method === "bank" && declineTarget.payment_status === "paid" && " The bank deposit will be marked as refund due."}</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">Keep</AlertDialogCancel>
            <AlertDialogAction className="bg-brand text-white hover:bg-[#d0141c]" data-testid="decline-confirm"
              onClick={() => { const t = declineTarget; setDeclineTarget(null); act(t, "decline", "Booking declined · customer emailed"); }}>Decline booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPage;
